// ============================================================
// authConfig.js
// MSAL configuration for Entra External ID
//
// This file configures the Microsoft Authentication Library (MSAL)
// to authenticate users against the A2Z CareHub External ID tenant
//
// The user flow carehub_signupsignin handles:
//   - Sign up with email and password
//   - Sign in with email and password
//   - Custom role attribute collection
// ============================================================

// ── Entra External ID tenant details ──────────────────────
const TENANT_ID = '31818735-8a3e-4c9c-a477-e7e25abd42ab';
const CLIENT_ID = '052d916f-1a14-46b5-95ff-c269847ca4ca';
const TENANT_DOMAIN = 'a2zcarehub.onmicrosoft.com';

// Authority URL for External ID — uses standard login.microsoftonline.com
// The user flow is configured on the app registration, not in the URL
const AUTHORITY = `https://a2zcarehub.ciamlogin.com`;

// Known authorities
const KNOWN_AUTHORITIES = ['a2zcarehub.ciamlogin.com'];

/**
 * Build the External ID end-session URL — the ONLY way to clear a
 * wedged CIAM session from outside the app.
 *
 * When a sign-in dies on Microsoft's error page (AADSTS165000, or the
 * empty account picker of Gotcha #80) the CareHub SPA never loads, so
 * MSAL's own logout is unreachable. Navigating here ends the External
 * ID session — clearing the remembered account whose staleness caused
 * it — and returns the user to a clean /login.
 *
 * @param {string} origin  e.g. window.location.origin
 * @returns {string}
 */
export function buildExternalIdLogoutUrl(origin) {
  const returnTo = encodeURIComponent(`${origin}/login`);
  return `${AUTHORITY}/${TENANT_ID}/oauth2/v2.0/logout?post_logout_redirect_uri=${returnTo}`;
}

// ── MSAL configuration ─────────────────────────────────────
export const msalConfig = {
  auth: {
    clientId: CLIENT_ID,
    authority: AUTHORITY,
    knownAuthorities: KNOWN_AUTHORITIES,
    redirectUri: window.location.origin, // http://localhost:3000 in dev
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'sessionStorage', // Cleared when browser tab closes
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (process.env.NODE_ENV === 'development') {
          console.log(`[MSAL] ${message}`);
        }
      },
    },
  },
};

// ── Login request ──────────────────────────────────────────
// Scopes requested during login
// openid, profile, email are standard OIDC scopes
export const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
};

// ── API request ────────────────────────────────────────────
// API scope for the CareHub user-tenant Function App endpoints
// (auth/me, carenotes/quality, agents/*, invitations/redeem).
// Separated from loginRequest so authentication (who are you)
// and authorisation (what API can you call) stay decoupled —
// sign-in only fetches OIDC scopes; the API token is acquired
// lazily on the first call via acquireTokenSilent. MSAL caches
// it per-scope and refreshes transparently.
//
// Value matches infra/bicep/modules/functions-flex.bicep
// JWT_USER_AUDIENCE (client ID GUID 120fb965-45a9-442f-b65e-
// 52a9b513cbf3 for api-carehub-user) + the exposed scope name
// `access_as_user` on the app registration. See
// docs/operations/jwt-cutover-runbook.md §A.5.
export const apiRequest = {
  scopes: ['api://api.a2zcarehub.co.uk/carehub-user/access_as_user'],
};

// ── Federated (direct-Entra) sign-in — Stage 2 dual-MSAL ────
// The SECOND PublicClientApplication path: the federated SPA app
// registration ("A2Z CareHub Federated Sign-In") authenticating
// client M365 users DIRECTLY against the Entra workforce
// /organizations endpoint — CareHub as its own OIDC Relying
// Party. Everything ABOVE (the External ID config) is untouched;
// this block is purely additive.
//
// DORMANCY: the federated clientId + workforce-API audience are
// injected at BUILD TIME as REACT_APP_FEDERATED_* env vars,
// sourced from kv-carehub-secrets (federatedSpaClientId /
// federatedApiAudience) by the pipeline — they are GUIDs and are
// NEVER hardcoded here. In the dormant prod build the pipeline
// does NOT inject them (the Stage-7 GO-LIVE gate, mirroring the
// functions-flex.bicep gate) -> both are undefined -> federatedConfig
// Present is false -> federatedMsalConfig is null -> index.js
// does not construct the federated instance. The federated path
// only goes live at the Stage 3 cutover.
const FEDERATED_SPA_CLIENT_ID = process.env.REACT_APP_FEDERATED_SPA_CLIENT_ID;
const FEDERATED_API_AUDIENCE = process.env.REACT_APP_FEDERATED_API_AUDIENCE;
const FEDERATED_AUTHORITY = 'https://login.microsoftonline.com/organizations';

// True only when BOTH federated identifiers were injected at
// build time — the single dormancy switch the rest of the SPA
// keys off.
export const federatedConfigPresent =
  typeof FEDERATED_SPA_CLIENT_ID === 'string' && FEDERATED_SPA_CLIENT_ID.length > 0 &&
  typeof FEDERATED_API_AUDIENCE === 'string' && FEDERATED_API_AUDIENCE.length > 0;

// MSAL config for the federated instance — null when dormant.
// redirectUri is `${origin}/login/org`, the DISTINCT path the
// "A2Z CareHub Federated Sign-In" app reg registered (FINDING
// gotcha #4 — the two instances must not race on one redirect
// path). `${origin}` resolves to whichever host serves the SPA,
// producing exactly one of the three registered redirect URIs:
// https://a2zcarehub.co.uk/login/org, https://app.a2zcarehub.co.uk
// /login/org, http://localhost:3000/login/org.
// knownAuthorities is deliberately NOT set — instance discovery
// covers the public workforce cloud (FINDING gotcha #7).
export const federatedMsalConfig = federatedConfigPresent ? {
  auth: {
    clientId: FEDERATED_SPA_CLIENT_ID,
    authority: FEDERATED_AUTHORITY,
    redirectUri: `${window.location.origin}/login/org`,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    // Same store as the External ID instance — sessionStorage.
    // Tokens are isolated automatically (MSAL cache keys are
    // clientId-scoped); account lists are environment-filtered in
    // code (isExternalIdAccount / isFederatedAccount below). NOT a
    // separate physical cache — that would change persistence
    // semantics. See FINDING gotcha #1.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (process.env.NODE_ENV === 'development') {
          console.log(`[MSAL:federated] ${message}`);
        }
      },
    },
  },
} : null;

// Login scopes for the federated instance — OIDC trio PLUS the
// workforce-API resource scope when active. Including the API scope
// in loginRedirect primes the MSAL cache with an access token for
// `api://${FEDERATED_API_AUDIENCE}/access_as_user` at sign-in time, so
// the first acquireTokenSilent (api.js getAccessToken) hits cache —
// never InteractionRequiredAuthError, never the popup/redirect
// fallback. Admin pre-consent on the client tenant (Stage 3 onboard)
// already covers the API scope, so no user-facing consent prompt
// fires. Secure-Score-blocked-popup-class fix; see api.js for the
// defence-in-depth redirect fallback. Falls back to the OIDC-only
// trio when dormant to preserve the dormant-build invariant.
export const federatedLoginRequest = federatedConfigPresent
  ? { scopes: ['openid', 'profile', 'email', `api://${FEDERATED_API_AUDIENCE}/access_as_user`] }
  : { scopes: ['openid', 'profile', 'email'] };

// API request for the federated instance — the WORKFORCE API
// scope. Requesting `api://<federatedApiAudience>/access_as_user`
// makes the issued access token's `aud` claim the workforce API
// app reg's appId GUID — exactly what withAuth's (dormant)
// direct-Entra branch validates (FINDING gotcha #5; CLAUDE.md
// Gotcha #21 — a v2 token's aud is the bare appId GUID, not the
// api:// URI). null when dormant.
export const federatedApiRequest = federatedConfigPresent
  ? { scopes: [`api://${FEDERATED_API_AUDIENCE}/access_as_user`] }
  : null;

// ── Account / authority classification ─────────────────────
// The MSAL account cache is SHARED across the two instances on
// one origin (FINDING gotcha #1 — the `msal.3.account.keys` index
// is not clientId-scoped), so getAllAccounts() on either instance
// returns BOTH authorities' accounts. These helpers filter by the
// account `environment` so each instance/path sees only its own
// accounts. NEVER use getAllAccounts()[0] unfiltered.
export function isExternalIdAccount(account) {
  return !!account
    && typeof account.environment === 'string'
    && account.environment.includes('ciamlogin');
}
export function isFederatedAccount(account) {
  return !!account
    && typeof account.environment === 'string'
    && (account.environment.includes('login.microsoftonline.com')
        || account.environment.includes('login.windows.net'));
}

// ── Role extraction ────────────────────────────────────────
// Extracts the role claim from the ID token
// Returns 'carer', 'manager', or null if not set
export function getRoleFromToken(account) {
  if (!account) return null;

  // Custom attributes in External ID are prefixed with
  // extension_{clientId without hyphens}_
  const clientIdClean = CLIENT_ID.replace(/-/g, '');
  const roleKey = `extension_${clientIdClean}_role`;

  // Check idTokenClaims
  const claims = account.idTokenClaims || {};

  // Try the extension attribute first
  if (claims[roleKey]) return claims[roleKey];

  // Fall back to plain 'role' claim
  if (claims['role']) return claims['role'];

  // Fall back to 'extension_role'
  if (claims['extension_role']) return claims['extension_role'];

  return null;
}